import { useContext, useEffect, useRef } from 'react';
import { Auth } from '../StateManagment/Auth';
import Location from './Location';
import callLogRecordOpen from '../helpers/clientRecordAudit/callLogRecordOpen';
import sentinelToEmpty from '../helpers/sentinelValue';

// Legt vast dat iemand een cliëntdossier heeft geopend.
//
// WAAROM DIT BESTAAT. De Cloud Audit Logs van Firestore vangen élke lees, maar
// bij een `Listen` (en dat is vrijwel alles hier) loggen ze de QUERY en niet de
// afzonderlijke documenten. Ze kunnen dus niet zeggen wélk dossier iemand heeft
// ingezien. Dit event vult dat gat.
//
// DRIE DINGEN DIE HIER PRECIES ZO MOETEN.
//
// 1. Alleen strings en booleans in de dependency-array. Een detailscherm rendert
//    opnieuw bij elke Firestore-snapshot; zet je daar een array of een hook-
//    resultaat in, dan telt de audittrail tientallen 'raadplegingen' voor één
//    keer kijken.
// 2. Een ref met de laatst gelogde sleutel, gezet vóór de async aanroep. React
//    17 verdubbelt effecten niet onder StrictMode, maar React 18 wel — dan
//    verdubbelt zonder deze ref stilletjes de hele audittrail.
// 3. De tenant komt uit de URL, niet uit `hooks/Client`: dat bestand leest
//    window.location één keer bij het laden van de bundle.
//
// Eén gemount scherm = één event. Twee events betekenen dus niet automatisch
// twee sessies: een herlaad is een nieuwe mount en dus terecht een nieuw event.
//
// AI-CONTEXT: See docs/features/clientdossier-auditlog.md

/**
 * 'undefined' en 'null' als TEKST komen echt voor: het id komt uit een URL-
 * segment en een kapotte link levert daar letterlijk die woorden op. Ze zijn
 * truthy en glippen door elke `if (!id)` heen.
 *
 * '' TELT HIER ALS SENTINEL, en dat deed het al: het stond niet in de eigen
 * lijst, maar een leeg id komt hieronder niet langs `if (!dossier)`. Het effect
 * is dat er geen event wordt geschreven — geen halve regel in de audittrail.
 *
 * De gedeelde lijst vangt nu ook 'nan', 'none' en hoofdletters. Een dossier-id
 * is een uuid, dus er sneuvelt geen echt id; wat verdwijnt is een audit-event
 * dat naar een dossier wijst dat niet bestaat.
 *
 * De `typeof`-guard blijft: `sentinelToEmpty` coerceert getallen naar tekst
 * omdat een BAG-id dat mag, maar een dossier-id van 123 is geen id.
 *
 * AI-CONTEXT: See docs/pitfalls.md#database-url-sentinel-string-persisted
 *
 * @param {unknown} waarde - Kandidaat-id uit de URL
 * @returns {string} Het opgeschoonde id, of '' als het onbruikbaar is
 */
const bruikbaarId = (waarde) => (typeof waarde === 'string' ? sentinelToEmpty(waarde) : '');

/**
 * @param {string} recordType - 'persona' of 'household' (zie helpers/clientRecordAudit/recordTypes.js)
 * @param {string} [recordId] - Het dossier-id, meestal `Location()[3]`
 * @returns {void}
 */
const useLogRecordOpen = (recordType, recordId) => {
    const [auth] = useContext(Auth);
    const tenant = bruikbaarId(Location()[1]);
    const dossier = bruikbaarId(recordId);

    // Auth moet klaar zijn: de callable hangt alleen een token aan als er op dat
    // moment iemand is ingelogd. Te vroeg vuren geeft 'permission-denied' en het
    // event verdwijnt stil.
    const actorKlaar = Boolean(auth);

    const gelogd = useRef('');

    useEffect(() => {
        const sleutel = `${recordType}:${dossier}`;
        if (!recordType || !dossier || !tenant || !actorKlaar) return;
        if (gelogd.current === sleutel) return;

        gelogd.current = sleutel;

        callLogRecordOpen({ client: tenant, recordType, recordId: dossier })
            .catch((e) => {
                // Bewuste, smalle uitzondering op pitfalls.md#error-catch-all: de
                // server gooit door (Error Reporting ziet het), maar het scherm
                // van een begeleider mag niet blokkeren op een loggingfout.
                console.warn('dossier-open niet gelogd', e);
            });
    }, [recordType, dossier, tenant, actorKlaar]);
};

export default useLogRecordOpen;
